'use client';

import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { InputProps } from 'supertokens-auth-react/lib/build/recipe/emailpassword/components/library/input';

import { Button, HeroIcon } from '@/components/atoms';
import { Dropdown } from '@/components/molecules';
import { fetchSiteCountries } from '@/lib/api';
import { useSiteBootstrap } from '@/lib/contexts/SiteBootstrapProvider';
import { useSiteConfig } from '@/lib/hooks/query';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils/tailwind';

export const CountrySelector = ({ value, onChange }: InputProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [countries, setCountries] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { tenantId } = useSiteBootstrap();
  const siteQuery = useSiteConfig(tenantId);
  const site = siteQuery.data;

  useEffect(() => {
    const fetchCountriesData = async () => {
      setLoading(true);
      try {
        const response = await fetchSiteCountries(
          site?.configuration.tenant_id ?? '',
        );
        const parsed = response.map(item => ({
          value: item.key.toString(),
          label: item.value,
        }));
        if (parsed.length === 0) {
          setLoading(false);
          return;
        }

        setCountries(parsed);

        if (!value && parsed.length > 0) {
          onChange(parsed[0].value);
        }

        setLoading(false);
      } catch (error) {
        onChange('error');
        setError(true);
        setLoading(false);
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-countrySelector',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      }
    };

    fetchCountriesData();
  }, [site?.configuration.tenant_id]);

  const selectedCountry =
    countries.find(c => c.value === value)?.label || countries[0]?.label;

  const handleSelectCountry = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  if (error || (!loading && countries.length === 0)) return null;

  return (
    <div data-supertokens="inputContainer">
      <Dropdown
        open={open}
        onOpenChange={() => setOpen(prev => !prev)}
        data-supertokens="input"
      >
        <Dropdown.Trigger
          as={Button}
          variant="secondary"
          className="normal-case"
          size="md"
          isLoading={loading}
          data-supertokens="dropDownTrigger"
          icon={
            <HeroIcon
              icon={ChevronDownIcon}
              className={cn(error && 'invisible')}
              size="small"
              style={{
                width: '1.5rem',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          }
        >
          {selectedCountry}
        </Dropdown.Trigger>
        {!error && (
          <Dropdown.Content
            align="start"
            className="z-3050 w-[368px] backdrop-blur-3xl max-h-96 overflow-y-auto overflow-x-hidden bg-(--drop-down-background-default) rounded-(--drop-down-radius)"
          >
            {countries.map(({ value, label }) => (
              <Dropdown.Item
                key={value}
                onClick={() => handleSelectCountry(value)}
                className={cn(
                  'rounded-(--drop-down-item-radius)',
                  'text-(--drop-down-item-text-color-default)',
                  'bg-(--drop-down-item-background-color-default)',
                  'hover:text-(--drop-down-item-background-color-default)',
                  'hover:bg-(--drop-down-item-text-color-default)',
                  {
                    'bg-(--drop-down-item-background-color-selected)! text-(--drop-down-item-text-color-selected)!':
                      selectedCountry === label,
                  },
                )}
                isActive={selectedCountry === label}
              >
                {label}
              </Dropdown.Item>
            ))}
          </Dropdown.Content>
        )}
      </Dropdown>
    </div>
  );
};
